import './ModalWindow.css';

import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { PortalToContainer } from 'components/PortalToContainer/PortalToContainer';
import { type FC, type ReactElement, memo, useEffect, useRef } from 'react';
import { isAncestorElement } from 'utils/isAncestorElement';

interface Props {
  confirmClose: boolean;
  container: HTMLElement | null;
  content: ReactElement;
  contentClassName?: string;
  footer?: ReactElement;
  footerClassName?: string;
  onClose: () => void;
  title?: string;
}

const BareModalWindow: FC<Props> = (props) => {
  const windowRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<boolean>(false);

  const handleClick = useStableCallback((e: MouseEvent) => {
    const element = e.target as HTMLElement;
    if (dragging.current) {
      dragging.current = false;
      return;
    }
    if (windowRef.current && !isAncestorElement(element, windowRef.current)) {
      if (!props.confirmClose) {
        e.preventDefault();
        e.stopPropagation();
      }
      props.onClose();
    }
  });

  const handleMouseEvent = useStableCallback((e: MouseEvent) => {
    const element = e.target as HTMLElement;
    // Prevent element click outside of window
    if (
      !props.confirmClose &&
      windowRef.current &&
      !isAncestorElement(element, windowRef.current)
    ) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  const handleDragStart = () => {
    dragging.current = true;
  };

  const handleDragEnd = () => {
    dragging.current = false;
  };

  useEffect(() => {
    const domEventListeners = new ManagedDomEventListeners();
    if (props.container) {
      domEventListeners.add(
        [props.container, 'click', handleClick, false],
        [props.container, 'mouseup', handleMouseEvent, false],
        [props.container, 'mousedown', handleMouseEvent, false],
        [props.container, 'mousemove', handleMouseEvent, false],
      );
    }

    return () => {
      domEventListeners.remove();
    };
  }, [props.container]);

  return (
    <PortalToContainer container={props.container}>
      <div
        className={classNames(
          'absolute',
          'flex',
          'flex-1',
          'justify-center',
          'items-center',
          'bg-transparent',
          'pointer-events-none',
          'inset-0',
          'ml-inherit',
        )}
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
      >
        <div
          className='modal-window box box-primary flex flex-col'
          ref={windowRef}
        >
          <div className='box-header with-border'>
            {props.title && <h3 className='box-title'>{props.title}</h3>}
            <div className='pull-right'>
              <Button
                variant='tertiary'
                icon='fa-xmark'
                size='xxs'
                onClick={props.onClose}
                type='button'
              />
            </div>
          </div>
          <div
            className={classNames(
              'box-body flex flex-1 flex-col overflow-hidden',
              props.contentClassName,
            )}
          >
            {props.content}
          </div>
          {props.footer && (
            <div
              className={classNames('box-footer', props.footerClassName, 'p-0')}
            >
              {props.footer}
            </div>
          )}
        </div>
      </div>
    </PortalToContainer>
  );
};

export const ModalWindow = memo(BareModalWindow);
